// File: src/pages/InstallationGuide.jsx

import React from 'react';
import {
  Page,
  Layout,
  Card,
  LegacyStack as Stack,
  Text,
  Button,
  List,
} from '@shopify/polaris';
import { useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate

// You might want to create a specific CSS file for this page, e.g., InstallationGuide.css
// import './InstallationGuide.css';

export default function InstallationGuide() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    // Check if it's likely the first route loaded in the session (key 'default')
    // or if the history is very short. If so, default to homepage ('/').
    if (location.key === 'default' || window.history.length <= 2) {
      navigate('/', { replace: true });
    } else {
      // Otherwise, safely go back one step in history
      navigate(-1);
    }
  };

  return (
    <Page fullWidth title="BaoBrain Installation & User Guide" backAction={{
      content: 'Back',
      onAction: handleBack,
    }}>
      <Layout>
       
        {/* Overview Section */}
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="extraLoose">
              <Text as="h2" variant="headingLg">
                1. Overview
              </Text>
              <Text as="p" variant="bodyMd">
                BaoBrain is your all-in-one analytics and optimization platform designed for small to medium-sized businesses.
                It automatically captures customer interactions, turns them into actionable insights, and helps you
                drive growth without guesswork.
              </Text>
              <Text as="p" variant="bodyMd">
                This guide is for any business owner who wants to leverage behavioral analytics and AI-driven insights
                to understand their customers better and boost their online store's performance.
              </Text>
              <Button primary onClick={() => navigate('/sites')}>
                Get Started Now →
              </Button>
            </Stack>
          </Card>
        </Layout.Section>

        {/* Installation Steps Section */}
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="extraLoose">
              <Text as="h2" variant="headingLg">
                2. Installation Steps
              </Text>
              <Text as="p" variant="bodyMd">
                Installing BaoBrain on your platform is quick and easy.
              </Text>
              <List type="number">
                <List.Item>
                  <strong>Go to your platform's App Marketplace:</strong> Log in to your store's admin panel,
                  then navigate to the apps section.
                </List.Item>
                <List.Item>
                  <strong>Search for BaoBrain:</strong> In the App Marketplace, use the search bar to find "BaoBrain".
                </List.Item>
                <List.Item>
                  <strong>Click 'Install':</strong> Select the BaoBrain app and click the 'Install' button.
                </List.Item>
                <List.Item>
                  <strong>Authorize Your Store:</strong> You will be redirected to BaoBrain to
                  authorize the connection between your store and BaoBrain. Follow the on-screen
                  prompts to grant the necessary permissions. This secure connection
                  allows BaoBrain to fetch necessary data and inject tracking scripts.
                </List.Item>
                <List.Item>
                  <strong>Wait for Setup to Complete:</strong> Once authorized, you will be redirected back
                  to BaoBrain. Our system will automatically inject the necessary tracking scripts into your
                  storefront. You'll see a confirmation once this process is complete.
                </List.Item>
              </List>
            </Stack>
          </Card>
        </Layout.Section>

        {/* GA4 Integration Section */}
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="extraLoose">
              <Text as="h2" variant="headingLg">
                3. GA4 Integration
              </Text>
              <Text as="p" variant="bodyMd">
                Once you connect your app to BaoBrain and create an account, you will need to integrate Google Analytics 4 (GA4). You'll be walked through this on the integrations page, but you will need to have a GA4 property set up for your site first. Here are the exact steps to create a new Google Analytics 4 (GA4) property for your website or store:
              </Text>
              <List type="number">
                <List.Item>
                  <strong>Step 1: Go to Google Analytics</strong><br />
                  Visit: https://analytics.google.com<br />
                  Log in with the Google account you want to use.
                </List.Item>
                <List.Item>
                  <strong>Step 2: Open the Admin Panel</strong><br />
                  In the bottom-left corner, click the gear icon to open Admin.
                </List.Item>
                <List.Item>
                  <strong>Step 3: Create New Property</strong><br />
                  Under the Account column, make sure your account is selected.<br />
                  In the Property column, click “+ Create Property.”
                </List.Item>
                <List.Item>
                  <strong>Step 4: Fill Out Property Details</strong><br />
                  Enter a property name (e.g., “My Shopify Store”)<br />
                  Choose your reporting time zone and currency<br />
                  Click “Next” and answer basic business info.
                </List.Item>
                <List.Item>
                  <strong>Step 5: Choose Data Stream Type</strong><br />
                  Select Web (for websites)<br />
                  Enter your Website URL and Stream Name<br />
                  Click “Create Stream”
                </List.Item>
                <List.Item>
                  <strong>Step 6: Copy the Measurement ID</strong><br />
                  You’ll now see a Measurement ID starting with G-XXXXXXXXXX<br />
                  This is what you’ll need to connect your site to BaoBrain.
                </List.Item>
              </List>
              <Text as="p" variant="bodyMd" id="ga4-notes">
                <strong>Notes:</strong><br />
                You may need to reconnect your Google account in BaoBrain after creating the property so it shows up.<br />
                It might take a few minutes for new properties to appear in the dropdown list.
              </Text>
            </Stack>
          </Card>
        </Layout.Section>

        {/* Post-Install Setup Section */}
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="extraLoose">
              <Text as="h2" variant="headingLg">
                4. Post-Install Setup
              </Text>
              <Text as="p" variant="bodyMd">
                After successful installation and authorization:
              </Text>
              <List>
                <List.Item>
                  <strong>Automatic Tracking:</strong> BaoBrain automatically injects lightweight,
                  asynchronous tracking scripts into your storefront. You do not need to
                  add any code manually.
                </List.Item>
                <List.Item>
                  <strong>Data Collection Begins:</strong> Data from your site visitors (clicks, scrolls, sessions, etc.)
                  will start collecting immediately.
                </List.Item>
                <List.Item>
                  <strong>Redirect to Dashboard:</strong> You will be automatically redirected to your BaoBrain dashboard,
                  where you can begin to see your initial insights.
                </List.Item>
                <List.Item>
                  <strong>Verification (Optional):</strong> To verify the script is working, open your store
                  in a new incognito browser tab and navigate through a few pages. Your actions should soon appear
                  in the "Recent Sessions" section of your BaoBrain dashboard.
                </List.Item>
              </List>
            </Stack>
          </Card>
        </Layout.Section>

        {/* Using the App Section */}
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="extraLoose">
              <Text as="h2" variant="headingLg">
                5. Using the App
              </Text>
              <Text as="p" variant="bodyMd">
                Access your BaoBrain dashboard anytime to gain insights into your store's performance and customer behavior.
              </Text>
              <List>
                <List.Item>
                  <strong>Accessing the Dashboard:</strong> You can access your BaoBrain dashboard directly
                  from the Apps section within your store's admin panel.
                </List.Item>
                <List.Item>
                  <strong>Dashboard Overview:</strong> The dashboard provides a comprehensive overview of:
                  <List type="bullet">
                    <List.Item>Key revenue and conversion metrics</List.Item>
                    <List.Item>Visual heatmaps showing visitor activity on your pages</List.Item>
                    <List.Item>Session recordings to replay user journeys</List.Item>
                    <List.Item>Drop-off alerts and funnel analysis</List.Item>
                    <List.Item>AI-driven insights and recommendations</List.Item>
                  </List>
                </List.Item>
                <List.Item>
                  <strong>Data Freshness:</strong> Data is updated in real-time for most metrics, with
                  detailed reports refreshing frequently.
                </List.Item>
                <List.Item>
                  <strong>Common Actions:</strong> Explore heatmaps for popular product pages, watch session
                  recordings of abandoned carts, or review your conversion funnel to identify friction points.
                </List.Item>
              </List>
            </Stack>
          </Card>
        </Layout.Section>

        {/* Troubleshooting / FAQ Section */}
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="extraLoose">
              <Text as="h2" variant="headingLg">
                6. Troubleshooting / FAQ
              </Text>
              <List>
                <List.Item>
                  <strong>"I don't see any data yet":</strong>
                  <List type="bullet">
                    <List.Item>Ensure your store is published and accessible.</List.Item>
                    <List.Item>Verify that the BaoBrain app is still 'Installed' in your App Marketplace.</List.Item>
                    <List.Item>If recently installed, allow a few minutes for initial data to appear.</List.Item>
                    <List.Item>Try refreshing your BaoBrain dashboard.</List.Item>
                  </List>
                </List.Item>
                <List.Item>
                  <strong>"Tracking script isn't loading":</strong>
                  <List type="bullet">
                    <List.Item>Confirm your store is online and not in maintenance mode.</List.Item>
                    <List.Item>Check your browser's developer console for any script errors.</List.Item>
                    <List.Item>Re-installing the app from the App Marketplace can sometimes resolve script injection issues.</List.Item>
                  </List>
                </List.Item>
              </List>
            </Stack>
          </Card>
        </Layout.Section>

        {/* Support Info Section */}
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="extraLoose">
              <Text as="h2" variant="headingLg">
                7. Support Information
              </Text>
              <Text as="p" variant="bodyMd">
                Need help? We're here for you!
              </Text>
              <List>
                <List.Item>
                  <strong>Email:</strong> <a href="mailto:support@baobrain.com">support@baobrain.com</a>
                </List.Item>
                <List.Item>
                  {/* Updated Help Page Link */}
                  <strong>Help Page:</strong> <a href="https://baobrain.com/ContactUs" target="_blank" rel="noopener noreferrer">https://baobrain.com/ContactUs</a>
                </List.Item>
                <List.Item>
                  <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM MST (Phoenix, AZ)
                </List.Item>
              </List>
            </Stack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}